import { getInitialMonths, getNextActiveMonth } from './useDatePicker.utils';
import type { MonthType } from './useDatePicker.utils';

export interface DatePickerState {
  activeMonths: MonthType[];
  hoveredDate: Date | null;
  focusedDate: Date | null;
}

export type DatePickerAction =
  | { type: 'SET_ACTIVE_MONTHS'; payload: MonthType[] }
  | { type: 'SET_HOVERED_DATE'; payload: Date | null }
  | { type: 'SET_FOCUSED_DATE'; payload: Date | null }
  | { type: 'GO_TO_PREVIOUS_MONTHS'; payload: { numberOfMonths: number } }
  | { type: 'GO_TO_PREVIOUS_MONTHS_BY_ONE'; payload: { numberOfMonths: number } }
  | { type: 'GO_TO_NEXT_MONTHS'; payload: { numberOfMonths: number } }
  | { type: 'GO_TO_NEXT_MONTHS_BY_ONE'; payload: { numberOfMonths: number } }
  | { type: 'GO_TO_DATE'; payload: { date: Date; numberOfMonths: number } }
  | { type: 'GO_TO_PREVIOUS_YEAR'; payload: { numberOfMonths: number; numYears?: number } }
  | { type: 'GO_TO_NEXT_YEAR'; payload: { numberOfMonths: number; numYears?: number } }
  | {
      type: 'FOCUS_DATE_WITH_MONTHS_UPDATE';
      payload: { date: Date; numberOfMonths: number; currentActiveMonths: MonthType[] };
    }
  | { type: 'CLEAR_FOCUS_AND_HOVER' };

export function datePickerReducer(
  state: DatePickerState,
  action: DatePickerAction
): DatePickerState {
  switch (action.type) {
    case 'SET_ACTIVE_MONTHS':
      return {
        ...state,
        activeMonths: action.payload
      };

    case 'SET_HOVERED_DATE':
      return {
        ...state,
        hoveredDate: action.payload
      };

    case 'SET_FOCUSED_DATE':
      return {
        ...state,
        focusedDate: action.payload
      };

    case 'GO_TO_PREVIOUS_MONTHS':
      return {
        ...state,
        activeMonths: getNextActiveMonth(state.activeMonths, action.payload.numberOfMonths, -1),
        focusedDate: null
      };

    case 'GO_TO_PREVIOUS_MONTHS_BY_ONE':
      return {
        ...state,
        activeMonths: getNextActiveMonth(state.activeMonths, action.payload.numberOfMonths, -1, 1),
        focusedDate: null
      };

    case 'GO_TO_NEXT_MONTHS':
      return {
        ...state,
        activeMonths: getNextActiveMonth(state.activeMonths, action.payload.numberOfMonths, 1),
        focusedDate: null
      };

    case 'GO_TO_NEXT_MONTHS_BY_ONE':
      return {
        ...state,
        activeMonths: getNextActiveMonth(state.activeMonths, action.payload.numberOfMonths, 1, 1),
        focusedDate: null
      };

    case 'GO_TO_DATE':
      return {
        ...state,
        activeMonths: getInitialMonths(action.payload.numberOfMonths, action.payload.date),
        focusedDate: null
      };

    case 'GO_TO_PREVIOUS_YEAR':
      const numYearsPrev = action.payload.numYears || 1;
      return {
        ...state,
        activeMonths: getNextActiveMonth(
          state.activeMonths,
          action.payload.numberOfMonths,
          -(numYearsPrev * 12 - action.payload.numberOfMonths + 1)
        ),
        focusedDate: null
      };

    case 'GO_TO_NEXT_YEAR':
      const numYearsNext = action.payload.numYears || 1;
      return {
        ...state,
        activeMonths: getNextActiveMonth(
          state.activeMonths,
          action.payload.numberOfMonths,
          numYearsNext * 12 - action.payload.numberOfMonths + 1
        ),
        focusedDate: null
      };

    case 'FOCUS_DATE_WITH_MONTHS_UPDATE':
      return {
        ...state,
        focusedDate: action.payload.date,
        activeMonths: getInitialMonths(
          action.payload.numberOfMonths,
          action.payload.date,
          action.payload.currentActiveMonths
        )
      };

    case 'CLEAR_FOCUS_AND_HOVER':
      return {
        ...state,
        focusedDate: null,
        hoveredDate: null
      };

    default:
      return state;
  }
}

export function createInitialDatePickerState(
  numberOfMonths: number,
  startDate: Date | null,
  initialVisibleMonth?: Date | null
): DatePickerState {
  return {
    activeMonths: startDate
      ? getInitialMonths(numberOfMonths, startDate)
      : getInitialMonths(numberOfMonths, initialVisibleMonth || null),
    hoveredDate: null,
    focusedDate: startDate
  };
}
